import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Spinner, Alert, Container } from 'react-bootstrap';
import { BsCalendar3, BsBarChart, BsPeople, BsCheckCircle } from 'react-icons/bs';
import { publicApi } from '../../api';

import logotipo_detic from '../../assets/img/logotipo_detic.png';

interface EstatisticasPublicas {
  totalProcessos: number;
  totalBancas: number;
  processosFinalizados: number;
  bancasEmAndamento: number;
  totalCandidatos: number;
  candidatosValidados: number;
  candidatosAtivos: number;
}

const EstatisticasPublicasComponent: React.FC = () => {
  const [estatisticas, setEstatisticas] = useState<EstatisticasPublicas | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEstatisticas = async () => {
      try {
        const response = await publicApi.get('/public/estatisticas');
        setEstatisticas(response.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro desconhecido');
      } finally {
        setLoading(false);
      }
    };

    fetchEstatisticas();
  }, []);





  if (loading) {
    return (
      <Container className="py-5" style={{ maxWidth: '1200px' }}>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <Spinner animation="border" role="status" style={{ color: '#2c3e50' }}>
              <span className="visually-hidden">Carregando...</span>
            </Spinner>
            <p className="text-muted mt-3">Carregando estatísticas...</p>
          </div>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5" style={{ maxWidth: '1200px' }}>
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-5 text-center">
            <Alert variant="danger" className="border-0">
              <Alert.Heading className="fw-light">Erro ao carregar estatísticas</Alert.Heading>
              <p className="mb-0">{error}</p>
            </Alert>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container className="py-5" style={{ maxWidth: '1200px' }}>
      {/* Header Section */}
      <div className="text-center mb-5">
        <div className="d-flex justify-content-center align-items-center mb-4">          
          <div>
            <h1 className="mb-0 fw-light" style={{ color: '#2c3e50', fontSize: '2.5rem' }}>Identifica</h1>
            <h4 className="text-muted fw-light mt-1">Heteroidentificação de Cotas Raciais</h4>
          </div>
        </div>        
        <p className="lead text-muted mx-auto" style={{ maxWidth: '800px', fontSize: '1.2rem' }}>
          Plataforma institucional para gestão de processos de heteroidentificação,
          garantindo transparência e eficiência na validação de candidatos
        </p>
      </div>

      {/* Statistics Title */}
      <div className="text-center mb-5">
        <h3 className="fw-light" style={{ color: '#2c3e50' }}>Estatísticas do Sistema</h3>
        <div className="mx-auto mt-2" style={{ width: '60px', height: '3px', backgroundColor: '#2c3e50' }}></div>
      </div>
      
      {/* Statistics Cards - First Row */}
      <Row className="mb-4">
        <Col md={6} lg={3} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="p-4 text-center">
              <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                <BsBarChart className="text-primary" size={24} />
              </div>
              <h2 className="fw-bold mb-2" style={{ color: '#2c3e50', fontSize: '2.5rem' }}>{estatisticas?.totalProcessos}</h2>
              <p className="text-muted mb-0 fw-semibold">Total de Processos</p>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6} lg={3} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="p-4 text-center">
              <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                <BsCheckCircle className="text-success" size={24} />
              </div>
              <h2 className="fw-bold mb-2" style={{ color: '#2c3e50', fontSize: '2.5rem' }}>{estatisticas?.processosFinalizados}</h2>
              <p className="text-muted mb-0 fw-semibold">Processos Finalizados</p>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6} lg={3} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="p-4 text-center">
              <div className="bg-info bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                <BsCalendar3 className="text-info" size={24} />
              </div>
              <h2 className="fw-bold mb-2" style={{ color: '#2c3e50', fontSize: '2.5rem' }}>{estatisticas?.totalBancas}</h2>
              <p className="text-muted mb-0 fw-semibold">Total de Bancas</p>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6} lg={3} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="p-4 text-center">
              <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                <BsBarChart className="text-warning" size={24} />
              </div>
              <h2 className="fw-bold mb-2" style={{ color: '#2c3e50', fontSize: '2.5rem' }}>{estatisticas?.bancasEmAndamento}</h2>
              <p className="text-muted mb-0 fw-semibold">Bancas em Andamento</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Statistics Cards - Second Row */}
      <Row className="mb-5">
        <Col md={4} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="p-4 text-center">
              <div className="bg-secondary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                <BsPeople className="text-secondary" size={24} />
              </div>
              <h2 className="fw-bold mb-2" style={{ color: '#2c3e50', fontSize: '2.5rem' }}>{estatisticas?.totalCandidatos}</h2>
              <p className="text-muted mb-0 fw-semibold">Total de Candidatos</p>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={4} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="p-4 text-center">
              <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                <BsCheckCircle className="text-success" size={24} />
              </div>
              <h2 className="fw-bold mb-2" style={{ color: '#2c3e50', fontSize: '2.5rem' }}>{estatisticas?.candidatosValidados}</h2>
              <p className="text-muted mb-0 fw-semibold">Candidatos Validados</p>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={4} className="mb-4">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="p-4 text-center">
              <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
                <BsPeople className="text-primary" size={24} />
              </div>
              <h2 className="fw-bold mb-2" style={{ color: '#2c3e50', fontSize: '2.5rem' }}>{estatisticas?.candidatosAtivos}</h2>
              <p className="text-muted mb-0 fw-semibold">Candidatos Ativos</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      

      
      {/* Footer */}
      <div className="text-center mt-5 pt-5">
        <div className="mx-auto mb-4" style={{ width: '60px', height: '1px', backgroundColor: '#dee2e6' }}></div>
        <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
          Desenvolvido por
        </p>
        <a href="https://detic.unicamp.br/" target="_blank" rel="noopener noreferrer" className="d-inline-block">
          <img 
            src={logotipo_detic} 
            alt="DETIC Logo" 
            style={{ 
              maxWidth: '120px', 
              height: 'auto',
              filter: 'grayscale(20%)',
              transition: 'filter 0.3s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.filter = 'grayscale(0%)'}
            onMouseLeave={(e) => e.currentTarget.style.filter = 'grayscale(20%)'}
          />
        </a>
      </div>
    </Container>
  );
};

export default EstatisticasPublicasComponent;